import React, { useEffect, useMemo, useRef, useState } from "react";
import getDataFromApi from "../../../components/getDataFromApi";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { LinkIcon } from "../../Common/SvgIcons/WorkflowIcons";

const SECTION_OPTIONS = [
    { label: "Any Sections", value: "" },
    { label: "Projects", value: "projects" },
    { label: "Evaluations", value: "evaluations" },
    { label: "Data Label", value: "data_label" },
    { label: "Training", value: "training" },
];

const TIME_OPTIONS = [
    { label: "Any Time", value: "" },
    { label: "Last 5 minutes", value: "5m" },
    { label: "Last 15 minutes", value: "15m" },
    { label: "Last 30 minutes", value: "30m" },
    { label: "Last 1 hour", value: "1h" },
    { label: "Last 3 hours", value: "3h" },
    { label: "Last 6 hours", value: "6h" },
    { label: "Last 12 hours", value: "12h" },
    { label: "Last 24 hours", value: "24h" },
    { label: "Last 2 days", value: "2d" },
    { label: "Last 7 days", value: "7d" },
    { label: "Custom range", value: "custom" },
];

const STATUS_CLASS_MAP = {
    complete: "py-1 px-2 small-font wf-icon-green",
    completed: "py-1 px-2 small-font wf-icon-green",
    terminated: "py-1 px-2 small-font wf-icon-red",
    failed: "py-1 px-2 small-font wf-icon-red",
    queued: "py-1 px-2 small-font wf-icon-blue",
    quued: "py-1 px-2 small-font wf-icon-blue", // keeping your backend spelling too, just in case
    evaluating: "py-1 px-2 small-font wf-icon-blue",
};

const getStatusClassName = (status) => {
    if (!status) return "";
    return STATUS_CLASS_MAP[String(status).toLowerCase()] || "";
};


const formatHeaderLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const formatExecutionTime = (value) => {
    if (!value) return "-";

    const [timePart] = String(value).split(".");
    return timePart;
};

const formatCellValue = (field, value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (field === "created") return formatDate(value);
    if (field === "data_size") return `${value} MB`;
    if (field === "execution_time") return formatExecutionTime(value);
    return value;
};

const formatDateTimeLocal = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
    )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getTimeRange = (value, customFrom, customTo) => {
    if (value === "custom") {
        return {
            from: customFrom ? new Date(customFrom).toISOString() : "",
            to: customTo ? new Date(customTo).toISOString() : "",
        };
    }

    if (!value) return { from: "", to: "" };

    const now = new Date();
    const from = new Date();

    switch (value) {
        case "5m":
            from.setMinutes(now.getMinutes() - 5);
            break;
        case "15m":
            from.setMinutes(now.getMinutes() - 15);
            break;
        case "30m":
            from.setMinutes(now.getMinutes() - 30);
            break;
        case "1h":
            from.setHours(now.getHours() - 1);
            break;
        case "3h":
            from.setHours(now.getHours() - 3);
            break;
        case "6h":
            from.setHours(now.getHours() - 6);
            break;
        case "12h":
            from.setHours(now.getHours() - 12);
            break;
        case "24h":
            from.setDate(now.getDate() - 1);
            break;
        case "2d":
            from.setDate(now.getDate() - 2);
            break;
        case "7d":
            from.setDate(now.getDate() - 7);
            break;
        default:
            break;
    }

    return {
        from: from.toISOString(),
        to: now.toISOString(),
    };
};

export default function HistorySubRow({ tenantId, viewFrom }) {
    const isAmbarellaCustomer =
        window.ENV?.CUSTOMER?.toLowerCase() === "ambarella";
    const sectionOptions = isAmbarellaCustomer
        ? SECTION_OPTIONS.filter((option) => option.value === "evaluations")
        : SECTION_OPTIONS;

    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [section, setSection] = useState(
        isAmbarellaCustomer ? "evaluations" : ""
    );
    const [user, setUser] = useState("");

    const [timeRange, setTimeRange] = useState("");
    const [appliedTimeRange, setAppliedTimeRange] = useState("");

    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [appliedCustomFrom, setAppliedCustomFrom] = useState("");
    const [appliedCustomTo, setAppliedCustomTo] = useState("");

    const [showCustomTime, setShowCustomTime] = useState(false);

    const customTimeRef = useRef(null);

    const navigate = useNavigate();

    const userName = localStorage.getItem('name')


    const isCustomRangeValid = useMemo(() => {
        if (!customFrom || !customTo) return false;
        return new Date(customFrom) <= new Date(customTo);
    }, [customFrom, customTo]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                customTimeRef.current &&
                !customTimeRef.current.contains(event.target)
            ) {
                setShowCustomTime(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!tenantId) {
            setRawData([]);
            setLoading(false);
            return;
        }

        const fetchSubTable = async () => {
            setLoading(true);

            const { from, to } = getTimeRange(
                appliedTimeRange,
                appliedCustomFrom,
                appliedCustomTo
            );

            const filters = {
                level: viewFrom === 'Tenant' ? 'tenant_admin' : viewFrom === 'User' ? 'user' : "system_admin",
                table_type: "sub_table",
                tenant_id: tenantId,
                ...(section && { section }),
                ...((viewFrom === "User" ? userName : user) && {
                    created_by: viewFrom === "User" ? userName : user,
                }),
                ...(from && { from }),
                ...(to && { to }),
            };

            try {
                const res = await getDataFromApi(
                    `${window.ENV.API_URL}/models?filters=${encodeURIComponent(
                        JSON.stringify(filters)
                    )}&section=evaluation_stats`,
                    "get"
                );

                const stats = res?.[tenantId]?.user_level_stats || {};

                const transformed = Object.entries(stats)
                    .map(([id, value]) => ({
                        id,
                        ...value,
                    }))
                    .sort((a, b) => new Date(b.created) - new Date(a.created));

                setRawData(transformed);
            } catch (error) {
                console.error("Failed to fetch sub table data:", error);
                setRawData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSubTable();
    }, [tenantId, section, user, appliedTimeRange, appliedCustomFrom, appliedCustomTo]);

    const userOptions = useMemo(() => {
        const users = Array.from(
            new Set(
                rawData
                    .map((item) => item.created_by)
                    .filter((u) => u && u !== "null")
            )
        );

        return [
            { label: "Any Users", value: "" },
            ...users.map((u) => ({ label: u, value: u })),
        ];
    }, [rawData]);

    const displayedData = useMemo(() => {
        if (!search.trim()) return rawData;

        const searchValue = search.toLowerCase();

        return rawData.filter((row) =>
            Object.values(row).some((value) =>
                String(value ?? "")
                    .toLowerCase()
                    .includes(searchValue)
            )
        );
    }, [rawData, search]);


    const tableColumns = useMemo(() => {
        if (!rawData.length) return [];

        const allKeys = new Set();
        rawData.forEach((row) => {
            Object.keys(row).forEach((key) => {
                if (key !== "id") allKeys.add(key); // hide id if you don't want it
            });
        });

        return Array.from(allKeys);
    }, [rawData]);

    const renderCellValue = (field, value) => {
        if (field === "status") {
            const statusClass = getStatusClassName(value);

            return (
                <span className={`status-pill ${statusClass}`}>
                    {value || "-"}
                </span>
            );
        }

        return formatCellValue(field, value);
    };

    const handleTimeChange = (value) => {
        setTimeRange(value);

        if (value === "custom") {
            if (!customFrom || !customTo) {
                const now = new Date();
                const before = new Date();
                before.setHours(now.getHours() - 24);

                setCustomFrom(formatDateTimeLocal(before));
                setCustomTo(formatDateTimeLocal(now));
            }
            setShowCustomTime(true);
            return;
        }

        setShowCustomTime(false);
        setAppliedTimeRange(value);
    };

    const handleApplyCustomTime = () => {
        if (!isCustomRangeValid) return;

        setAppliedCustomFrom(customFrom);
        setAppliedCustomTo(customTo);
        setAppliedTimeRange("custom");
        setShowCustomTime(false);
    };

    const openEvaluationResults = (row) => {
        const modelName = String(row?.model_tested || "").trim();
        const evaluationId = row?.id;

        if (!modelName || !evaluationId) return;

        const params = new URLSearchParams();
        if (row?.compression != null && String(row.compression).trim()) {
            params.set("compression", String(row.compression).trim());
        }

        const query = params.toString();
        navigate(
            `/hub/${encodeURIComponent(modelName)}/evaluation${query ? `?${query}` : ""}`,
            {
                state: { evalId: evaluationId, viewFrom: "admin" },
            }
        );
    };

    return (
        <div className="container-card p-3 bg-transparent my-2">
            <div className="history-toolbar">


                <div className="history-search-wrap d-a gap-2 p-2 badge-border px-3">
                    <FontAwesomeIcon className="plain-button2 text-secondary" icon={faSearch} />
                    <input
                        className="border-0 m-0 bg-transparent text w-100"
                        style={{ outline: "none" }}
                        placeholder="Search by name, model, device, user, status..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="history-filter-group">
                    <select
                        className="history-filter-select text"
                        value={section}
                        onChange={(e) => setSection(e.target.value)}
                    >
                        {sectionOptions.map((option) => (
                            <option className="container-card text" key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <div className="history-time-dropdown-wrap" ref={customTimeRef}>
                        <select
                            className="history-filter-select text"
                            value={timeRange}
                            onChange={(e) => handleTimeChange(e.target.value)}
                        >
                            {TIME_OPTIONS.map((option) => (
                                <option className="container-card text" key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        {showCustomTime && (
                            <div className="history-time-popover">
                                <div className="history-time-popover-title">Custom time range</div>

                                <div className="d-a gap-2">
                                    <label>From</label>
                                    <input
                                        type="datetime-local"
                                        className="container-card text px-3 py-1 rounded w-100"
                                        value={customFrom}
                                        onChange={(e) => setCustomFrom(e.target.value)}
                                    />
                                </div>

                                <div className="d-a gap-2 my-2">
                                    <label>To</label>
                                    <input
                                        type="datetime-local"
                                        className="container-card text px-3 py-1 rounded w-100"
                                        value={customTo}
                                        onChange={(e) => setCustomTo(e.target.value)}
                                    />
                                </div>

                                {!isCustomRangeValid && (
                                    <div className="text-error">
                                        Enter a valid From and To range.
                                    </div>
                                )}

                                <div className="history-time-actions">
                                    <button
                                        type="button"
                                        className="history-apply-btn"
                                        onClick={handleApplyCustomTime}
                                        disabled={!isCustomRangeValid}
                                    >
                                        Apply time range
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {viewFrom !== "User" && (
                        <select
                            className="history-filter-select text"
                            value={user}
                            onChange={(e) => setUser(e.target.value)}
                        >
                            {userOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="py-3 text-center">Loading details...</div>
            ) : !displayedData.length ? (
                <div className="py-3 text-center">No details found.</div>
            ) : (
                <div className="table-wrap">
                    <table className="tenant-table w-100">
                        <thead>
                            <tr>
                                {tableColumns.map((field) => (
                                    <th className="text-nowrap container-card" style={{ borderSpacing: '5px' }} key={field}>{formatHeaderLabel(field)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {displayedData.map((row) => (
                                <tr key={row.id} >
                                    {tableColumns.map((field, index) => (
                                        <td
                                            key={field}
                                            onClick={
                                                index === 0
                                                    ? () => openEvaluationResults(row)
                                                    : undefined
                                            }
                                            style={{
                                                cursor: index === 0 ? "pointer" : "default",
                                            }}
                                        >
                                            {index === 0 ? (
                                                <div className="d-a gap-2">
                                                    {renderCellValue(field, row[field])}
                                                    <LinkIcon className="wf-icon-small text-primary" />
                                                </div>
                                            ) : (
                                                renderCellValue(field, row[field])
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
